import { Link, useLocation } from 'react-router-dom';
import * as api from '../../api/client';
import { useIncidentRateReport } from '../../hooks/useReports';
import { useReportParams } from './ScopeRangePicker';
import { Card } from '../common/Card';
import { Skeleton } from '../common/Skeleton';
import { formatDurationSeconds } from '../../utils/series';
import { DeltaValue } from './DeltaValue';
import { CoverageNote } from './CoverageNote';

const TREND_TONE: Record<string, string> = {
  rising: 'text-status-down',
  falling: 'text-status-up',
  flat: 'text-gray-500 dark:text-gray-400',
};

export function IncidentRateReport() {
  const { query } = useReportParams();
  const { data, isLoading, isError } = useIncidentRateReport(query);
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading report">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }
  if (isError || !data) {
    return <Card className="p-10 text-center text-gray-500 dark:text-gray-400">Could not load this report.</Card>;
  }
  if (data.rows.length === 0) {
    return <Card className="p-10 text-center text-gray-500 dark:text-gray-400">No services match this scope.</Card>;
  }
  if (data.rows.every(r => r.incident_count === 0)) {
    return <Card className="p-10 text-center text-gray-500 dark:text-gray-400">No incidents in this period.</Card>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <CoverageNote coverage={data.meta.coverage} />
        <a
          href={api.reportCsvUrl('incidents', query)}
          download
          className="no-print text-sm text-brand-600 dark:text-brand-400 hover:underline"
        >
          Export CSV
        </a>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th scope="col" className="text-left px-4 py-3">Service</th>
              <th scope="col" className="text-right px-2 py-3">Incidents</th>
              <th scope="col" className="text-right px-2 py-3">Trend</th>
              <th scope="col" className="text-right px-2 py-3">MTTR</th>
              <th scope="col" className="text-right px-2 py-3">MTBF</th>
              <th scope="col" className="text-right px-2 py-3">Longest outage</th>
              <th scope="col" className="text-right px-4 py-3">Total downtime</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {data.rows.map(r => (
              <tr key={r.service_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="px-4 py-2.5">
                  <Link
                    to={{ pathname: `/reports/services/${r.service_id}`, search: location.search }}
                    className="text-brand-600 dark:text-brand-400 hover:underline"
                  >
                    {r.service_name}
                  </Link>
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">
                  {r.incident_count}
                  {r.incident_delta !== undefined && r.incident_delta !== null && (
                    <div><DeltaValue value={r.incident_delta} decimals={0} invert /></div>
                  )}
                </td>
                <td className="px-2 py-2.5 text-right">
                  {r.trend && (
                    <span className={`text-xs font-medium ${TREND_TONE[r.trend]}`}>{r.trend}</span>
                  )}
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">
                  {formatDurationSeconds(r.mttr_seconds)}
                  {r.mttr_delta !== undefined && r.mttr_delta !== null && (
                    <div><DeltaValue value={r.mttr_delta} unit="s" decimals={0} invert /></div>
                  )}
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">
                  {formatDurationSeconds(r.mtbf_seconds)}
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">
                  {formatDurationSeconds(r.longest_seconds)}
                </td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums">
                  {formatDurationSeconds(r.downtime_seconds)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
